import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  RefreshCw,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { ReportFiltersState } from '@/features/reports/components/filters/ReportFilters';
import { useLocalizationQuality } from '@/hooks';
import { useLocaleAttributes } from '@/hooks/useAnalyticsReports';
import { createLogger } from '@/lib/logger';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { useTheme } from '@/contexts/ThemeContext';

interface LocalizationCoverageCardProps {
  filters: ReportFiltersState;
}

// Create component-specific logger
const localizationCoverageLogger = createLogger('LocalizationCoverageCard');

/**
 * Enterprise-ready card that displays localization coverage by organization locales
 * Shows translation completeness for each locale with visual indicators and trends
 */
const LocalizationCoverageCard: React.FC<LocalizationCoverageCardProps> = ({ filters }) => {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();

  // State for expanded locales
  const [expandedLocales, setExpandedLocales] = useState<Set<string>>(new Set());

  // Fetch data using our custom hook
  const { data, isLoading, isError, error, refetch } = useLocalizationQuality(filters);

  // Fetch locale attributes data (includes family filter)
  const { data: localeAttributesData } = useLocaleAttributes({
    locale: filters.locale,
    family: filters.family,
  });

  // Helper to get locale display name with flag emoji
  const getLocaleDisplayInfo = (localeCode: string) => {
    const localeMap: Record<string, { name: string; flag: string; region: string }> = {
      en_US: { name: 'English (US)', flag: '🇺🇸', region: 'North America' },
      fr_FR: { name: 'French', flag: '🇫🇷', region: 'Europe' },
      es_ES: { name: 'Spanish', flag: '🇪🇸', region: 'Europe' },
      de_DE: { name: 'German', flag: '🇩🇪', region: 'Europe' },
      de_AT: { name: 'German (Austria)', flag: '🇦🇹', region: 'Europe' },
      fr_CH: { name: 'French (Switzerland)', flag: '🇨🇭', region: 'Europe' },
      it_IT: { name: 'Italian', flag: '🇮🇹', region: 'Europe' },
      ja_JP: { name: 'Japanese', flag: '🇯🇵', region: 'Asia Pacific' },
      ko_KR: { name: 'Korean', flag: '🇰🇷', region: 'Asia Pacific' },
      pt_BR: {
        name: 'Portuguese (Brazil)',
        flag: '🇧🇷',
        region: 'South America',
      },
      ru_RU: { name: 'Russian', flag: '🇷🇺', region: 'Europe' },
      zh_CN: {
        name: 'Chinese (Simplified)',
        flag: '🇨🇳',
        region: 'Asia Pacific',
      },
    };
    return localeMap[localeCode] || { name: localeCode, flag: '🌐', region: 'Other' };
  };

  // Helper to get completion status badge
  const getCompletionBadge = (percentage: number) => {
    if (percentage >= 90) {
      return (
        <Badge variant="default" className="border-green-200 bg-green-100 text-green-800">
          {i18n.getLocalizationCoverageCardText.completionBadges.excellent}
        </Badge>
      );
    } else if (percentage >= 70) {
      return (
        <Badge variant="default" className="border-blue-200 bg-blue-100 text-blue-800">
          {i18n.getLocalizationCoverageCardText.completionBadges.good}
        </Badge>
      );
    } else if (percentage >= 50) {
      return (
        <Badge variant="default" className="border-yellow-200 bg-yellow-100 text-yellow-800">
          {i18n.getLocalizationCoverageCardText.completionBadges.fair}
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive">
          {i18n.getLocalizationCoverageCardText.completionBadges.needsWork}
        </Badge>
      );
    }
  };

  // Helper to get trend icon (placeholder for future trend data)
  const getTrendIcon = (percentage: number) => {
    if (percentage >= 80) {
      return <TrendingUp className="size-4 text-green-600" />;
    } else if (percentage >= 60) {
      return <Minus className="size-4 text-gray-500" />;
    } else {
      return <TrendingDown className="size-4 text-red-600" />;
    }
  };

  // Process and sort locale data
  const processedLocales = useMemo(() => {
    if (!data?.locale_stats || !Array.isArray(data.locale_stats)) {
      return [];
    }

    try {
      let locales = data.locale_stats
        .map(locale => ({
          ...locale,
          displayInfo: getLocaleDisplayInfo(locale.locale),
          completionPercentage: Math.round(locale.translated_pct || 0),
        }))
        .sort((a, b) => b.completionPercentage - a.completionPercentage); // Sort by completion percentage descending

      // Filter by selected locale if specified
      if (filters.locale) {
        locales = locales.filter(locale => locale.locale === filters.locale);
      }

      return locales;
    } catch (err) {
      localizationCoverageLogger.error('Error processing locale data', {}, err as Error);
      return [];
    }
  }, [data, filters.locale]);

  // Toggle expanded state for a locale
  const toggleLocaleExpanded = (localeCode: string) => {
    const newExpanded = new Set(expandedLocales);
    if (newExpanded.has(localeCode)) {
      newExpanded.delete(localeCode);
    } else {
      newExpanded.add(localeCode);
    }
    setExpandedLocales(newExpanded);
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!processedLocales.length) {
      return {
        totalLocales: 0,
        averageCompletion: 0,
        excellentCount: 0,
        needsWorkCount: 0,
      };
    }

    const totalLocales = processedLocales.length;
    const averageCompletion = Math.round(
      processedLocales.reduce((sum, locale) => sum + locale.completionPercentage, 0) / totalLocales
    );
    const excellentCount = processedLocales.filter(
      locale => locale.completionPercentage >= 90
    ).length;
    const needsWorkCount = processedLocales.filter(
      locale => locale.completionPercentage < 50
    ).length;

    return { totalLocales, averageCompletion, excellentCount, needsWorkCount };
  }, [processedLocales]);

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Globe className="size-5 text-blue-600" />
            <CardTitle>{i18n.getLocalizationCoverageCardText.title}</CardTitle>
          </div>
          {!isLoading && !isError && (
            <Badge variant="outline" className="text-xs">
              {i18n.getLocalizationCoverageCardText.localesCount(summaryStats.totalLocales)}
            </Badge>
          )}
        </div>
        <CardDescription>{i18n.getLocalizationCoverageCardText.subtitle}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          // Loading state - Skeleton placeholders
          <div className="space-y-4">
            <div className="mb-6 grid grid-cols-2 gap-4">
              <Skeleton className="h-16 rounded-lg" />
              <Skeleton className="h-16 rounded-lg" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <Skeleton className="size-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-2 w-full" />
                  </div>
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          // Error state
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>{i18n.getLocalizationCoverageCardText.errors.errorLoadingData}</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <p>{i18n.getLocalizationCoverageCardText.errors.problemFetching}</p>
              {error instanceof Error && <p className="text-xs opacity-80">{error.message}</p>}
              <Button variant="outline" size="sm" className="mt-2 w-fit" onClick={() => refetch()}>
                <RefreshCw className="mr-2 size-4" />
                {i18n.getLocalizationCoverageCardText.errors.retry}
              </Button>
            </AlertDescription>
          </Alert>
        ) : processedLocales.length > 0 ? (
          // Main content
          <div className="space-y-6">
            {/* Summary Statistics */}
            <div className="grid grid-cols-2 gap-4">
              <div
                className={`rounded-lg border p-4 ${
                  actualTheme === 'dark'
                    ? 'border-blue-700 bg-gradient-to-r from-blue-900/30 to-blue-800/30'
                    : 'border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        actualTheme === 'dark' ? 'text-blue-200' : 'text-blue-900'
                      }`}
                    >
                      {i18n.getLocalizationCoverageCardText.averageCompletion}
                    </p>
                    <p
                      className={`text-2xl font-bold ${
                        actualTheme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                      }`}
                    >
                      {summaryStats.averageCompletion}%
                    </p>
                  </div>
                  <div className={actualTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>
                    {getTrendIcon(summaryStats.averageCompletion)}
                  </div>
                </div>
              </div>
              <div
                className={`rounded-lg border p-4 ${
                  actualTheme === 'dark'
                    ? 'border-green-700 bg-gradient-to-r from-green-900/30 to-green-800/30'
                    : 'border-green-200 bg-gradient-to-r from-green-50 to-green-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        actualTheme === 'dark' ? 'text-green-200' : 'text-green-900'
                      }`}
                    >
                      {i18n.getLocalizationCoverageCardText.excellentLocales}
                    </p>
                    <p
                      className={`text-2xl font-bold ${
                        actualTheme === 'dark' ? 'text-green-300' : 'text-green-700'
                      }`}
                    >
                      {summaryStats.excellentCount}/{summaryStats.totalLocales}
                    </p>
                  </div>
                  <div className={actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'}>
                    <TrendingUp className="size-5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Locale List */}
            <div className="space-y-3">
              <h4
                className={`mb-3 text-sm font-semibold ${
                  actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}
              >
                {filters.locale
                  ? i18n.getLocalizationCoverageCardText.localeDetails(
                      getLocaleDisplayInfo(filters.locale).name
                    )
                  : i18n.getLocalizationCoverageCardText.localesByCompletion}
              </h4>
              {processedLocales.map((locale, _index) => {
                const isExpanded = expandedLocales.has(locale.locale);
                const leastCompletedAttributes = localeAttributesData?.[locale.locale] || [];

                return (
                  <div
                    key={locale.locale}
                    className={`overflow-hidden rounded-lg border ${
                      actualTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}
                  >
                    <div
                      className={`flex cursor-pointer items-center space-x-4 p-3 transition-colors ${
                        actualTheme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleLocaleExpanded(locale.locale)}
                    >
                      {/* Flag and Locale Info */}
                      <div className="flex min-w-0 flex-1 items-center space-x-3">
                        <div className="text-2xl">{locale.displayInfo.flag}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <p
                              className={`truncate text-sm font-medium ${
                                actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                              }`}
                            >
                              {locale.displayInfo.name}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {locale.displayInfo.region}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center space-x-2">
                            <Progress value={locale.completionPercentage} className="h-2 flex-1" />
                            <span
                              className={`min-w-12 text-right text-xs ${
                                actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`}
                            >
                              {locale.completionPercentage}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Completion Badge and Stats */}
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p
                            className={`text-xs ${
                              actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}
                          >
                            {locale.translated_attributes?.toLocaleString() || 0} /{' '}
                            {locale.total_attributes?.toLocaleString() || 0}
                          </p>
                          <p
                            className={`text-xs ${
                              actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            {i18n.getLocalizationCoverageCardText.attributes}
                          </p>
                        </div>
                        {getCompletionBadge(locale.completionPercentage)}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0"
                          onClick={e => {
                            e.stopPropagation();
                            toggleLocaleExpanded(locale.locale);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Content - Top 5 Least Completed Attributes */}
                    {isExpanded && (
                      <div
                        className={`border-t p-4 ${
                          actualTheme === 'dark'
                            ? 'border-gray-700 bg-gray-800/50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <h5
                          className={`mb-3 text-sm font-medium ${
                            actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                          }`}
                        >
                          {i18n.getLocalizationCoverageCardText.topLeastCompleted}
                        </h5>
                        <div className="space-y-2">
                          {leastCompletedAttributes.map((attr, attrIndex) => (
                            <div
                              key={attrIndex}
                              className={`flex items-center justify-between rounded border p-2 ${
                                actualTheme === 'dark'
                                  ? 'border-gray-600 bg-gray-900/50'
                                  : 'border bg-white'
                              }`}
                            >
                              <div className="flex-1">
                                <p
                                  className={`text-sm font-medium ${
                                    actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                                  }`}
                                >
                                  {attr.name}
                                </p>
                                <div className="mt-1 flex items-center space-x-2">
                                  <Progress value={attr.completion} className="h-1.5 flex-1" />
                                  <span
                                    className={`min-w-12 text-right text-xs ${
                                      actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                    }`}
                                  >
                                    {attr.completion}%
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 text-right">
                                <p className="text-xs font-medium text-red-600">
                                  {attr.missing} {i18n.getLocalizationCoverageCardText.missing}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action Footer */}
            <div
              className={`border-t pt-4 ${
                actualTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}
            >
              <div
                className={`flex items-center justify-between text-xs ${
                  actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                <span>
                  {summaryStats.needsWorkCount > 0 && (
                    <span className="text-amber-600">
                      {i18n.getLocalizationCoverageCardText.needAttention(
                        summaryStats.needsWorkCount
                      )}
                    </span>
                  )}
                  {summaryStats.needsWorkCount === 0 && (
                    <span className="text-green-600">
                      {i18n.getLocalizationCoverageCardText.allPerformingWell}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ) : (
          // No data state
          <div className="py-8 text-center">
            <Globe
              className={`mx-auto mb-4 size-12 ${
                actualTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
            <h3
              className={`mb-2 text-sm font-medium ${
                actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}
            >
              {i18n.getLocalizationCoverageCardText.noData.title}
            </h3>
            <p
              className={`mb-4 text-sm ${
                actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              {i18n.getLocalizationCoverageCardText.noData.message}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 size-4" />
              {i18n.getLocalizationCoverageCardText.noData.refreshData}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LocalizationCoverageCard;
